import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import Modal from '../components/Modal';
import CacheStatusBar from '../components/CacheStatusBar';
import LabelPrintModal from '../components/LabelPrintModal';
import StockAuditModal from '../components/StockAuditModal';
import StockAuditTabView from '../components/StockAuditTabView';
import SocialPhotographyModal from '../components/SocialPhotographyModal';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import './InventoryPage.css';
import Pagination from '../components/Pagination';

const ADJUSTMENT_REASON_OPTIONS = [
  { value: 'Damaged', permissionId: 'inventory.adjustStock.damaged' },
  { value: 'Lost', permissionId: 'inventory.adjustStock.lost' },
  { value: 'Correction', permissionId: 'inventory.adjustStock.correction' },
  { value: 'Split Piece', permissionId: 'inventory.adjustStock.split' },
  { value: 'Use in VR Production', permissionId: 'inventory.adjustStock.correction' },
];

const isDisplayableInventoryPiece = (piece) => {
  const status = String(piece?.status || 'active').trim().toLowerCase();
  const sourceType = String(piece?.sourceType || '').trim().toUpperCase();
  const length = Number(piece?.length || 0);

  if (!Number.isFinite(length) || length <= 0) return false;
  if (status && status !== 'active') return false;
  if (piece?.isDeleted === true || piece?.deleted === true) return false;
  if (piece?.needsReview === true) return false;
  if (sourceType === 'BATCH_ORDER_ALLOCATION_VOIDED') return false;

  return true;
};

const displayPieceCount = (item) => {
  const totalLength = Number(item?.totalLength || 0);
  if (!Number.isFinite(totalLength) || totalLength <= 0) return 0;
  return Number(item?.pieceCount || 0);
};

function InventoryPage() {
  const apiBase =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : '';

  const { currentUser, canPerformAction, activeFirebaseApp } = useAuth();
  const dataCache = useDataCache();
  const SHOW_UNDO_IMPORT_BUTTON = false;
  const SHOW_PURGE_USAGE_BUTTON = false;
  const SHOW_INVENTORY_SUMMARY_SECTION = false;
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory' | 'stock_audit'
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [expandedLedgerRowId, setExpandedLedgerRowId] = useState(null);
  const [expandedSummarySku, setExpandedSummarySku] = useState(null);
  const [expandedSearchSku, setExpandedSearchSku] = useState(null);
  const [searchSkuImageMap, setSearchSkuImageMap] = useState({});
  const [searchSort, setSearchSort] = useState({ key: 'sku', dir: 'asc' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  // New state for search results
  const [searchResults, setSearchResults] = useState(null);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [groupedSearchResults, setGroupedSearchResults] = useState(null);
  const [isGroupingSearch, setIsGroupingSearch] = useState(false);
  const [groupedSearchError, setGroupedSearchError] = useState('');

  const [pieceDetails, setPieceDetails] = useState([]);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  // New state for piece history
  const [selectedPiece, setSelectedPiece] = useState(null); // e.g. { pieceId: 'BIN1', sku: 'SKU1' }
  const [pieceHistory, setPieceHistory] = useState([]);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Form state
  const [sku, setSku] = useState('');
  const [pieceId, setPieceId] = useState('');
  const [length, setLength] = useState('');
  const [remark, setRemark] = useState('');
  const [skuSuggestions, setSkuSuggestions] = useState([]);
  const [isFetchingSkuSuggestions, setIsFetchingSkuSuggestions] = useState(false);
  const [skuSuggestionError, setSkuSuggestionError] = useState('');
  const [selectedSku, setSelectedSku] = useState(null);
  const [showSkuDropdown, setShowSkuDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [formError, setFormError] = useState('');
  const [isPurgingUsage, setIsPurgingUsage] = useState(false);

  // New state for stock adjustment
  const [pieceToAdjust, setPieceToAdjust] = useState(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('Damaged');
  const [isSubmittingAdjustment, setIsSubmittingAdjustment] = useState(false);
  const [newPieceIdForSplit, setNewPieceIdForSplit] = useState('');
  const [adjustmentError, setAdjustmentError] = useState('');
  const [correctionDeduct, setCorrectionDeduct] = useState('');
  const [correctionAdd, setCorrectionAdd] = useState('');
  const [adjustmentRemark, setAdjustmentRemark] = useState('');

  // New state for undoing an adjustment
  const [isUndoingAdjustment, setIsUndoingAdjustment] = useState(false);
  const [undoingLedgerId, setUndoingLedgerId] = useState(null);

  // New state for global ledger
  const [globalLedger, setGlobalLedger] = useState([]);
  const [isFetchingGlobalLedger, setIsFetchingGlobalLedger] = useState(true);
  const [globalLedgerError, setGlobalLedgerError] = useState('');
  const [globalLedgerCurrentPage, setGlobalLedgerCurrentPage] = useState(1);
  const [globalLedgerTotalItems, setGlobalLedgerTotalItems] = useState(0);
  const globalLedgerItemsPerPage = 100;
  const [deletingLedgerId, setDeletingLedgerId] = useState(null);
  const [recentActivityFilter, setRecentActivityFilter] = useState('all'); // all | new | used | restock | batchReceived | adjustment | correction
  const globalLedgerCacheKey = `inventoryLedger:/api/v1/inventory/ledger?page=${globalLedgerCurrentPage}&limit=${globalLedgerItemsPerPage}`;

  // New state for visibility
  const [showInventorySummary, setShowInventorySummary] = useState(false);
  const showRecentActivity = true;
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showSocialPhotographyModal, setShowSocialPhotographyModal] = useState(false);
  const [showLabelPrintModal, setShowLabelPrintModal] = useState(false);
  const [showStockAuditModal, setShowStockAuditModal] = useState(false);
  const [bundleSuggestions, setBundleSuggestions] = useState([]);
  const [isLoadingBundleSuggestions, setIsLoadingBundleSuggestions] = useState(false);
  const [bundleSuggestionsError, setBundleSuggestionsError] = useState('');
  const [selectedBundleSuggestion, setSelectedBundleSuggestion] = useState(null);
  const [bundleBuildQty, setBundleBuildQty] = useState('1');
  const [bundleBuildError, setBundleBuildError] = useState('');
  const [isBuildingBundle, setIsBuildingBundle] = useState(false);
  const [showBundleSuggestions, setShowBundleSuggestions] = useState(false);
  const [bundleComponentPieces, setBundleComponentPieces] = useState({});
  const [bundleComponentLoading, setBundleComponentLoading] = useState({});
  const [expandedBundleComponents, setExpandedBundleComponents] = useState({});
  const [showSkuGroupSection, setShowSkuGroupSection] = useState(false);
  const [showSkuGroupForm, setShowSkuGroupForm] = useState(false);
  const [skuGroups, setSkuGroups] = useState([]);
  const [isLoadingSkuGroups, setIsLoadingSkuGroups] = useState(false);
  const [skuGroupsError, setSkuGroupsError] = useState('');
  const [skuGroupMessage, setSkuGroupMessage] = useState('');
  const [isSavingSkuGroup, setIsSavingSkuGroup] = useState(false);
  const [skuInputValue, setSkuInputValue] = useState('');
  const [skuInputOptions, setSkuInputOptions] = useState([]);
  const [isSkuInputSearching, setIsSkuInputSearching] = useState(false);

  const hasLegacyAdjustPermission = canPerformAction('inventory.adjustStock');
  const allowedAdjustmentReasons = ADJUSTMENT_REASON_OPTIONS.filter(
    (opt) => hasLegacyAdjustPermission || canPerformAction(opt.permissionId)
  );
  const canAdjustAny = allowedAdjustmentReasons.length > 0;
  const defaultAdjustmentReason = allowedAdjustmentReasons[0]?.value || 'Damaged';
  const [groupDraftSkus, setGroupDraftSkus] = useState([]);
  const [showQuarantineQueue, setShowQuarantineQueue] = useState(false);
  const [includeResolvedQuarantine, setIncludeResolvedQuarantine] = useState(false);
  const [quarantineQueue, setQuarantineQueue] = useState([]);
  const [isLoadingQuarantineQueue, setIsLoadingQuarantineQueue] = useState(false);
  const [quarantineQueueError, setQuarantineQueueError] = useState('');
  const [quarantineActionById, setQuarantineActionById] = useState({});

  // State for conditional import/undo buttons
  const [lastImportId, setLastImportId] = useState(null);
  const [isCheckingImport, setIsCheckingImport] = useState(true);
  const canViewQuarantineQueue = canPerformAction('inventory.quarantineQueueView');
  const canActOnQuarantineQueue = canPerformAction('inventory.quarantineQueueAct');

  const invalidateInventoryCaches = useCallback(() => {
    dataCache.invalidate('inventoryPieces:/api/v1/inventory/all-pieces');
    dataCache.invalidatePrefix('inventorySummary:');
    dataCache.invalidatePrefix('inventorySummarySearch:');
    dataCache.invalidatePrefix('inventoryLedger:');
    dataCache.invalidatePrefix('inventoryBundleSuggestions:');
    dataCache.invalidatePrefix('inventorySkuGroups:');
    dataCache.invalidatePrefix('orders:');
  }, [dataCache]);

  const fetchSkuGroups = useCallback(async ({ force = false } = {}) => {
    if (!currentUser) return;
    setIsLoadingSkuGroups(true);
    setSkuGroupsError('');
    try {
      const cacheKey = 'inventorySkuGroups:/api/v1/sku-groups';
      const cachedEntry = dataCache.getCacheEntry(cacheKey);
      const cachedData = cachedEntry?.value;
      if (Array.isArray(cachedData?.groups)) {
        setSkuGroups(cachedData.groups);
        if (!force && !cachedEntry.stale) return;
      }

      const { data } = await dataCache.fetchJsonCached(cacheKey, '/api/v1/sku-groups', { force });
      setSkuGroups(Array.isArray(data?.groups) ? data.groups : []);
    } catch (err) {
      setSkuGroupsError(err.message || 'Failed to load SKU groups.');
    } finally {
      setIsLoadingSkuGroups(false);
    }
  }, [currentUser, dataCache]);

  useEffect(() => {
    if (!showSkuGroupSection) return;
    fetchSkuGroups();
  }, [showSkuGroupSection, fetchSkuGroups]);

  const fetchQuarantineQueue = useCallback(async ({ force = false, includeResolved = includeResolvedQuarantine } = {}) => {
    if (!currentUser || !canViewQuarantineQueue) return;
    setIsLoadingQuarantineQueue(true);
    setQuarantineQueueError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${apiBase}/api/v1/inventory/quarantine-queue?includeResolved=${includeResolved ? 'true' : 'false'}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data?.success !== true) {
        throw new Error(data?.message || 'Failed to fetch quarantine queue.');
      }
      setQuarantineQueue(Array.isArray(data.entries) ? data.entries : []);
    } catch (err) {
      setQuarantineQueueError(err.message || 'Failed to fetch quarantine queue.');
    } finally {
      setIsLoadingQuarantineQueue(false);
    }
  }, [apiBase, canViewQuarantineQueue, currentUser, includeResolvedQuarantine]);

  useEffect(() => {
    if (!showQuarantineQueue || !canViewQuarantineQueue) return;
    fetchQuarantineQueue({ includeResolved: includeResolvedQuarantine });
  }, [showQuarantineQueue, canViewQuarantineQueue, includeResolvedQuarantine, fetchQuarantineQueue]);

  const runQuarantineQueueAction = useCallback(
    async (queueId, endpoint, body = {}, pendingLabel = 'Saving...') => {
      if (!currentUser || !queueId) return;
      setQuarantineActionById((prev) => ({ ...prev, [queueId]: pendingLabel }));
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${apiBase}/api/v1/inventory/quarantine-queue/${encodeURIComponent(queueId)}/${endpoint}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data?.success !== true) {
          throw new Error(data?.message || 'Action failed.');
        }
        await fetchQuarantineQueue({ force: true, includeResolved: includeResolvedQuarantine });
      } catch (err) {
        alert(err.message || 'Failed to perform quarantine action.');
      } finally {
        setQuarantineActionById((prev) => {
          const next = { ...prev };
          delete next[queueId];
          return next;
        });
      }
    },
    [apiBase, currentUser, fetchQuarantineQueue, includeResolvedQuarantine]
  );

  const fetchExistingSkuOptions = useCallback(async (query) => {
    if (!currentUser) return [];
    const q = String(query || '').trim().toUpperCase();
    if (!q) return [];
    const token = await currentUser.getIdToken();
    const resp = await fetch(
      `${apiBase}/api/v1/skus/search?query=${encodeURIComponent(q)}&limit=60`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) return [];
    const data = await resp.json().catch(() => ({}));
    return Array.isArray(data?.skus)
      ? data.skus.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean).slice(0, 50)
      : [];
  }, [apiBase, currentUser]);

  const normalizeSkuValue = (value) => String(value || '').trim().toUpperCase().replace(/\s+/g, '');

  const registerSkuIfNeeded = useCallback(async (rawSku) => {
    if (!currentUser) throw new Error('You must be logged in.');
    const sku = normalizeSkuValue(rawSku);
    if (!sku) throw new Error('SKU is required.');
    if (!/^[A-Z0-9._/-]+$/.test(sku)) {
      throw new Error('Invalid SKU format. Allowed: A-Z, 0-9, . _ / -');
    }
    const token = await currentUser.getIdToken();
    const resp = await fetch(`${apiBase}/api/v1/skus/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ sku }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data?.success === false) {
      throw new Error(data?.message || `Failed to register SKU (${resp.status}).`);
    }
    return sku;
  }, [apiBase, currentUser]);

  const handleSkuInputChange = async (value) => {
    const sku = normalizeSkuValue(value);
    setSkuInputValue(sku);
    if (sku.length < 2) {
      setSkuInputOptions([]);
      setIsSkuInputSearching(false);
      return;
    }
    setIsSkuInputSearching(true);
    try {
      const options = await fetchExistingSkuOptions(sku);
      setSkuInputOptions(options);
    } catch (_) {
      setSkuInputOptions([]);
    } finally {
      setIsSkuInputSearching(false);
    }
  };

  const addSkuToDraftGroup = () => {
    const sku = normalizeSkuValue(skuInputValue);
    if (!sku) {
      setSkuGroupMessage('Select an existing SKU first.');
      return;
    }
    if (!/^[A-Z0-9._/-]+$/.test(sku)) {
      setSkuGroupMessage('Invalid SKU format. Allowed: A-Z, 0-9, . _ / -');
      return;
    }
    setGroupDraftSkus((prev) => {
      if (prev.includes(sku)) return prev;
      return [...prev, sku];
    });
    registerSkuIfNeeded(sku).catch(() => {});
    setSkuInputValue('');
    setSkuInputOptions([]);
    setSkuGroupMessage('');
  };

  const removeSkuFromDraftGroup = (sku) => {
    const target = String(sku || '').toUpperCase();
    setGroupDraftSkus((prev) => prev.filter((x) => x !== target));
  };

  const createSkuGroupFromExistingSkus = async () => {
    if (!currentUser) return;
    const uniqueDraftSkus = [...new Set(groupDraftSkus.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean))];
    if (uniqueDraftSkus.length < 2) {
      setSkuGroupMessage('Add at least 2 existing SKUs.');
      return;
    }

    const sorted = [...uniqueDraftSkus].sort();
    const groupName = `${sorted.length} SKU Group`;

    setIsSavingSkuGroup(true);
    setSkuGroupMessage('');
    try {
      const token = await currentUser.getIdToken();
      // Backend auto-generates small running group ID when groupId is omitted.
      const createResponse = await fetch(`${apiBase}/api/v1/sku-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: groupName,
          active: true,
          allowSubstitutionForOrders: true,
        }),
      });

      const createData = await createResponse.json().catch(() => ({}));
      if (!createResponse.ok || createData.success === false) {
        throw new Error(createData.message || `Failed to create group (${createResponse.status}).`);
      }
      const groupId = String(createData.groupId || '').trim();
      if (!groupId) {
        throw new Error('Group ID was not returned by server.');
      }

      const response = await fetch(`${apiBase}/api/v1/sku-groups/${encodeURIComponent(groupId)}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          members: sorted.map((sku, idx) => ({
            sku,
            rank: idx + 1,
            isPrimary: idx === 0,
            substitutionAllowed: true,
            active: true,
          })),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || `Failed to add SKUs to group (${response.status}).`);
      }
      setSkuGroupMessage(`Group created: ${groupId}`);
      setSkuInputValue('');
      setSkuInputOptions([]);
      setGroupDraftSkus([]);
      await fetchSkuGroups({ force: true });
    } catch (err) {
      setSkuGroupMessage(err.message || 'Failed to group SKUs.');
    } finally {
      setIsSavingSkuGroup(false);
    }
  };

  const fetchBundleSuggestions = useCallback(async ({ force = false } = {}) => {
    if (!currentUser) return;
    setIsLoadingBundleSuggestions(true);
    setBundleSuggestionsError('');
    try {
      const cacheKey = 'inventoryBundleSuggestions:/api/v1/inventory/bundles/suggestions';
      const cachedEntry = dataCache.getCacheEntry(cacheKey);
      const cachedData = cachedEntry?.value;
      if (Array.isArray(cachedData?.suggestions)) {
        setBundleSuggestions(cachedData.suggestions);
        if (!force && !cachedEntry.stale) {
          return;
        }
      }
      const { data } = await dataCache.fetchJsonCached(cacheKey, '/api/v1/inventory/bundles/suggestions', { force });
      setBundleSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
    } catch (err) {
      setBundleSuggestionsError(err.message || 'Failed to load bundle suggestions.');
    } finally {
      setIsLoadingBundleSuggestions(false);
    }
  }, [currentUser, dataCache]);

  const fetchInventory = useCallback(async (page) => {
    if (!currentUser) return;
    setIsLoading(true);
    try {
      const cacheKey = `inventorySummary:/api/v1/inventory/summary?page=${page}&limit=${itemsPerPage}`;
      const cachedEntry = dataCache.getCacheEntry(cacheKey);
      const cachedData = cachedEntry?.value;
      if (Array.isArray(cachedData?.inventorySummary) && cachedData.inventorySummary.length > 0) {
        setInventory(cachedData.inventorySummary);
        setTotalItems(cachedData.totalCount || 0);
        if (!cachedEntry.stale) {
          return;
        }
      }

      const { data } = await dataCache.fetchJsonCached(
        cacheKey,
        `/api/v1/inventory/summary?page=${page}&limit=${itemsPerPage}`,
        { force: false }
      );
      setInventory(data.inventorySummary);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, dataCache, itemsPerPage]);

  const fetchSearchResults = useCallback(async (page, search, sortState = null) => {
    const trimmed = String(search || '').trim();
    const sortKey = String(sortState?.key || 'sku').trim();
    const sortDir = String(sortState?.dir || 'asc').trim();
    if (!currentUser || !trimmed) {
      setSearchResults([]);
      setTotalSearchResults(0);
      setGroupedSearchResults(null);
      return;
    }
    if (trimmed.length < 3) {
      setSearchResults([]);
      setTotalSearchResults(0);
      setGroupedSearchResults(null);
      setError('Type at least 3 characters to search.');
      return;
    }
    setIsSearching(true);
    setError(''); // Clear main error
    // Avoid stale grouped state masking fresh search rows.
    setGroupedSearchResults(null);
    try {
      const encoded = encodeURIComponent(trimmed);
      const cacheKey = `inventorySummarySearch:/api/v1/inventory/summary?page=${page}&limit=${itemsPerPage}&search=${encoded}&sort=${encodeURIComponent(sortKey)}&dir=${encodeURIComponent(sortDir)}`;
      const cachedEntry = dataCache.getCacheEntry(cacheKey);
      const cachedData = cachedEntry?.value;
      if (Array.isArray(cachedData?.inventorySummary)) {
        setSearchResults(cachedData.inventorySummary);
        setTotalSearchResults(cachedData.totalCount || 0);
        if (!cachedEntry.stale) {
          return;
        }
      }
      const { data } = await dataCache.fetchJsonCached(
        cacheKey,
        `/api/v1/inventory/summary?page=${page}&limit=${itemsPerPage}&search=${encoded}&sort=${encodeURIComponent(sortKey)}&dir=${encodeURIComponent(sortDir)}`,
        { force: false }
      );
      setSearchResults(data.inventorySummary);
      setTotalSearchResults(data.totalCount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSearching(false);
    }
  }, [currentUser, dataCache, itemsPerPage]);

  const fetchInventorySummaryRowBySku = useCallback(async (token, skuUpper) => {
    const resp = await fetch(
      `${apiBase}/api/v1/inventory/summary?page=1&limit=100&search=${encodeURIComponent(skuUpper)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) return null;
    const data = await resp.json().catch(() => ({}));
    const rows = Array.isArray(data?.inventorySummary) ? data.inventorySummary : [];
    const exact = rows.find((r) => String(r?.sku || '').trim().toUpperCase() === skuUpper);
    if (!exact) return null;
    return {
      sku: String(exact.sku || '').trim().toUpperCase(),
      totalLength: Number(exact.totalLength || 0),
      pieceCount: Number(exact.pieceCount || 0),
    };
  }, [apiBase]);

  const buildGroupedSearchRows = useCallback(async () => {
    if (!currentUser || !Array.isArray(searchResults)) {
      setGroupedSearchResults(null);
      setGroupedSearchError('');
      return;
    }
    setIsGroupingSearch(true);
    setGroupedSearchError('');
    try {
      const token = await currentUser.getIdToken();
      const baseRows = searchResults.map((r) => ({
        sku: String(r?.sku || '').trim().toUpperCase(),
        totalLength: Number(r?.totalLength || 0),
        pieceCount: Number(r?.pieceCount || 0),
      })).filter((r) => r.sku);
      const baseBySku = new Map(baseRows.map((r) => [r.sku, r]));
      const summaryCache = new Map(baseRows.map((r) => [r.sku, r]));
      const processedGroupIds = new Set();
      const grouped = [];

      const getRowBySku = async (skuUpper) => {
        if (summaryCache.has(skuUpper)) return summaryCache.get(skuUpper);
        const row = await fetchInventorySummaryRowBySku(token, skuUpper);
        summaryCache.set(skuUpper, row);
        return row;
      };

      for (const row of baseRows) {
        const resolveResp = await fetch(
          `${apiBase}/api/v1/sku-groups/resolve?sku=${encodeURIComponent(row.sku)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resolveResp.ok) {
          grouped.push(row);
          continue;
        }
        const resolveData = await resolveResp.json().catch(() => ({}));
        const group = resolveData?.group;
        const members = Array.isArray(resolveData?.members) ? resolveData.members : [];
        const isGrouped = resolveData?.grouped === true && group && members.length > 0;

        if (!isGrouped) {
          grouped.push(row);
          continue;
        }

        const groupId = String(group.groupId || group.id || '').trim();
        if (!groupId) {
          grouped.push(row);
          continue;
        }
        if (processedGroupIds.has(groupId)) continue;
        processedGroupIds.add(groupId);

        const memberSkus = [...new Set(members
          .filter((m) => m?.active !== false)
          .map((m) => String(m?.sku || '').trim().toUpperCase())
          .filter(Boolean))];

        const memberRows = [];
        for (const memberSku of memberSkus) {
          const memberRow = baseBySku.get(memberSku) || await getRowBySku(memberSku);
          if (memberRow) memberRows.push(memberRow);
        }

        if (memberRows.length === 0) {
          grouped.push(row);
          continue;
        }

        const totalLength = memberRows.reduce((sum, m) => sum + Number(m.totalLength || 0), 0);
        const pieceCount = memberRows.reduce((sum, m) => sum + Number(m.pieceCount || 0), 0);
        grouped.push({
          sku: row.sku,
          totalLength,
          pieceCount,
          _group: {
            groupId,
            name: group.name || null,
            members: memberRows,
          },
        });
      }

      setGroupedSearchResults(grouped);
    } catch (err) {
      setGroupedSearchError(err.message || 'Failed to build grouped stock view.');
      setGroupedSearchResults(null);
    } finally {
      setIsGroupingSearch(false);
    }
  }, [apiBase, currentUser, fetchInventorySummaryRowBySku, searchResults]);

  useEffect(() => {
    if (showInventorySummary) {
      fetchInventory(currentPage);
    }
  }, [fetchInventory, currentPage, showInventorySummary]);

  useEffect(() => {
    buildGroupedSearchRows();
  }, [searchResults, buildGroupedSearchRows]);

  // Listen to the import metadata document in Firestore
  useEffect(() => {
    if (!currentUser) return;

    const db = getFirestore(activeFirebaseApp);
    const metadataRef = doc(db, 'system_metadata', 'inventory_metadata');
    setIsCheckingImport(true);

    const unsubscribe = onSnapshot(metadataRef, (docSnap) => {
      if (docSnap.exists()) {
        setLastImportId(docSnap.data().lastImportId || null);
      } else {
        setLastImportId(null);
      }
      setIsCheckingImport(false);
    }, (err) => {
      console.error("Error listening to import status:", err);
      setIsCheckingImport(false);
    });

    return () => unsubscribe();
  }, [currentUser, activeFirebaseApp]);

  const fetchGlobalLedger = useCallback(async (page, { force = false } = {}) => {
    if (!currentUser) return;
    // Don't set to true initially, only when fetching
    setIsFetchingGlobalLedger(true);
    setGlobalLedgerError('');
    try {
      const cacheKey = `inventoryLedger:/api/v1/inventory/ledger?page=${page}&limit=${globalLedgerItemsPerPage}`;
      const cachedEntry = dataCache.getCacheEntry(cacheKey);
      const cachedData = cachedEntry?.value;
      if (Array.isArray(cachedData?.ledger) && cachedData.ledger.length > 0) {
        const cachedWithDates = cachedData.ledger.map((entry) => ({
          ...entry,
          timestamp: new Date(entry.timestamp),
        }));
        setGlobalLedger(cachedWithDates);
        setGlobalLedgerTotalItems(cachedData.totalCount || 0);
        setIsFetchingGlobalLedger(false);
        if (!force && !cachedEntry.stale) {
          return;
        }
      }

      const { data } = await dataCache.fetchJsonCached(
        cacheKey,
        `/api/v1/inventory/ledger?page=${page}&limit=${globalLedgerItemsPerPage}`,
        { force }
      );
      const historyWithDates = data.ledger.map(entry => ({
        ...entry,
        timestamp: new Date(entry.timestamp)
      }));
      setGlobalLedger(historyWithDates);
      setGlobalLedgerTotalItems(data.totalCount || 0);
    } catch (err) {
      setGlobalLedgerError(err.message);
    } finally {
      setIsFetchingGlobalLedger(false);
    }
  }, [currentUser, dataCache, globalLedgerItemsPerPage]);

  useEffect(() => {
    if (showRecentActivity) {
      fetchGlobalLedger(globalLedgerCurrentPage);
    }
  }, [fetchGlobalLedger, globalLedgerCurrentPage, showRecentActivity]);

  useEffect(() => {
    if (!showBundleSuggestions) return;
    fetchBundleSuggestions();
  }, [showBundleSuggestions, fetchBundleSuggestions]);

  useEffect(() => {
    setGlobalLedgerCurrentPage(1);
  }, [recentActivityFilter]);

  const handleGlobalLedgerPageChange = (pageNumber) => {
    setGlobalLedgerCurrentPage(pageNumber);
  };

  const fetchSkuSuggestions = useCallback(async (query) => {
    if (!currentUser) return;
    setIsFetchingSkuSuggestions(true);
    setSkuSuggestionError('');
    const normalizedQuery = normalizeSkuValue(query);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/skus/search?query=${encodeURIComponent(normalizedQuery)}&limit=60`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        let message = 'Failed to search products.';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errData = await response.json();
            message = errData.message || message;
          } else {
            const text = await response.text();
            message = text ? `${message} (${text.slice(0, 120)}${text.length > 120 ? '...' : ''})` : message;
          }
        } catch (_) {
          // ignore parse errors, use default message
        }
        throw new Error(`${message} [${response.status}]`);
      }
      const data = await response.json();
      let suggestions = Array.isArray(data?.skus)
        ? data.skus.map((skuVal) => ({
            sku: normalizeSkuValue(skuVal),
            productTitle: '',
            variantTitle: '',
            imageSrc: '',
          }))
        : [];
      suggestions = suggestions.filter((s) => s.sku.includes(normalizedQuery));
      setSkuSuggestions(suggestions);
      setShowSkuDropdown(suggestions.length > 0);
      if (suggestions.length === 0) {
        setSkuSuggestionError('No SKU found. You can still add this as a new SKU.');
      }
    } catch (err) {
      setSkuSuggestionError(err.message || 'Could not search products right now.');
      setSkuSuggestions([]);
    } finally {
      setIsFetchingSkuSuggestions(false);
    }
  }, [currentUser]);

  useEffect(() => {
    const trimmedSku = normalizeSkuValue(sku);
    if (selectedSku?.sku === trimmedSku && trimmedSku) {
      setSkuSuggestions([]);
      setShowSkuDropdown(false);
      setSkuSuggestionError('');
      return;
    }
    if (!trimmedSku) {
      setSkuSuggestions([]);
      setSelectedSku(null);
      setShowSkuDropdown(false);
      setSkuSuggestionError('');
      return;
    }
    const handler = setTimeout(() => {
      if (trimmedSku.length < 2) {
        setSkuSuggestions([]);
        setSkuSuggestionError('Keep typing to search SKU...');
        return;
      }
      fetchSkuSuggestions(trimmedSku);
    }, 300);
    return () => clearTimeout(handler);
  }, [sku, fetchSkuSuggestions, selectedSku]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchPageChange = (pageNumber) => {
    setSearchCurrentPage(pageNumber);
  };

  const handleSearchSubmit = () => {
    const trimmed = String(searchInput || '').trim();
    if (trimmed.length < 3) {
      setError('Type at least 3 characters to search.');
      return;
    }
    setCurrentPage(1); // Reset to first page on new search
    setSubmittedSearch(trimmed);
    setSearchCurrentPage(1);
    setSearchResults([]); // Show the results section immediately
    setGroupedSearchResults(null);
    setGroupedSearchError('');
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSubmittedSearch('');
    setSearchResults(null); // Hide the results section
    setSearchCurrentPage(1);
    setTotalSearchResults(0);
    setGroupedSearchResults(null);
    setGroupedSearchError('');
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    const normalizedSku = normalizeSkuValue(sku);
    if (!normalizedSku) {
      setFormError('Please enter a SKU.');
      return;
    }
    if (!/^[A-Z0-9._/-]+$/.test(normalizedSku)) {
      setFormError('Invalid SKU format. Allowed: A-Z, 0-9, . _ / -');
      return;
    }
    setIsSubmitting(true);
    setFormError('');

    try {
      await registerSkuIfNeeded(normalizedSku);
      const payload = { sku: normalizedSku, pieceId, length: parseFloat(length) };
      if (remark.trim()) {
        payload.remark = remark.trim();
      }

      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/inventory/pieces', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

	      const data = await response.json();
	      if (!response.ok) {
	        throw new Error(data.message || 'Failed to add stock.');
	      }

      invalidateInventoryCaches();
      alert('Stock added successfully!');
      // Reset form and refresh inventory
      setSku('');
      setLength('');
      setRemark('');
      setSelectedSku(null);
      setSkuSuggestions([]);
      await fetchNextPieceId();
      if (showInventorySummary) fetchInventory(1);
      if (showRecentActivity) fetchGlobalLedger(1);
      setCurrentPage(1);
      // No need to setGlobalLedgerCurrentPage(1) here as fetchGlobalLedger(1) already handles it.
      setGlobalLedgerCurrentPage(1);

	    } catch (err) {
	      setFormError(err.message);
	    } finally {
	      setIsSubmitting(false);
	    }
	  };

	  const handleUndoImport = async () => {
    if (!window.confirm('Are you sure you want to undo the last stock import? This will delete all pieces added in the last import.')) {
        return;
    }
    setIsUndoing(true);
    setFormError(''); // Reuse formError for this action's errors

    try {
        const token = await currentUser.getIdToken();
        const response = await fetch('/api/v1/inventory/undo-bulk-import', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
        });

	        const data = await response.json();
	        if (!response.ok) {
	            throw new Error(data.message || 'Failed to undo import.');
	        }

	        invalidateInventoryCaches();
	        alert(data.message);
	        if (showInventorySummary) fetchInventory(currentPage);
	        if (showRecentActivity) fetchGlobalLedger(1);
        // No need to setGlobalLedgerCurrentPage(1) here as fetchGlobalLedger(1) already handles it.
        setGlobalLedgerCurrentPage(1);
    } catch (err) {
        setFormError(`Undo Error: ${err.message}`);
	    } finally {
	        setIsUndoing(false);
	    }
	  };

	  const handlePurgeUsage = async () => {
    if (!window.confirm('Delete all Used / Correction / Restock ledger entries and reverse their inventory impact? This cannot be undone.')) {
      return;
    }
    setIsPurgingUsage(true);
    setFormError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/inventory/purge-usage', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
	      const data = await response.json();
	      if (!response.ok) {
	        throw new Error(data.message || 'Failed to purge usage entries.');
	      }
	      invalidateInventoryCaches();
	      alert(data.message || 'Purged usage entries.');
	      if (showInventorySummary) fetchInventory(currentPage);
	      if (showRecentActivity) fetchGlobalLedger(1);
      setGlobalLedgerCurrentPage(1);
    } catch (err) {
      setFormError(`Purge Error: ${err.message}`);
    } finally {
      setIsPurgingUsage(false);
    }
  };

  const handleExportCSV = async () => {
    let piecesToExport = [];
    try {
      const token = await currentUser.getIdToken();
      const allPiecesResponse = await fetch('/api/v1/inventory/all-pieces', {
          headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!allPiecesResponse.ok) {
          throw new Error('Could not fetch data for export.');
      }
      const allPiecesData = await allPiecesResponse.json();
      piecesToExport = allPiecesData.pieces;
    } catch (err) {
      alert(err.message);
      return;
    }
    if (piecesToExport.length === 0) {
      alert('No inventory pieces to export.');
      return;
    }

    const headers = ["SKU", "Piece ID", "Length", "Location", "Status", "Created At"];
    const rows = piecesToExport.map(p => [
      p.sku,
      p.pieceId,
      p.length,
      p.location || '',
      p.status,
      p.createdAt ? new Date(p.createdAt._seconds * 1000).toISOString() : ''
    ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'inventory-stock.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportActivityCSV = async () => {
    try {
      const token = await currentUser.getIdToken();
      const days = 30;
      const limit = 5000;
      const response = await fetch(`${apiBase}/api/v1/inventory/ledger/export?days=${days}&limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || 'Failed to export activity.');
        }
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to export activity.');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory-activity-last-${days}d.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Failed to export activity.');
    }
  };

	  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.trim().split('\n');
        const headers = lines.shift().trim().toLowerCase().split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        const skuIndex = headers.indexOf('sku');
        const pieceIdIndex = headers.indexOf('piece id');
        const lengthIndex = headers.indexOf('length');

        if (skuIndex === -1 || pieceIdIndex === -1 || lengthIndex === -1) {
          throw new Error('CSV must contain "SKU", "Piece ID", and "Length" columns.');
        }

        const importedPieces = lines.map(line => {
          const values = line.split(',');
          return { sku: values[skuIndex]?.trim().replace(/^"|"$/g, ''), pieceId: values[pieceIdIndex]?.trim().replace(/^"|"$/g, ''), length: values[lengthIndex]?.trim().replace(/^"|"$/g, '') };
        }).filter(p => p.sku && p.pieceId && p.length);

        if (importedPieces.length === 0) { throw new Error('No valid rows found in the CSV to import.'); }

        setIsSubmitting(true);
        const token = await currentUser.getIdToken();
	        const response = await fetch('/api/v1/inventory/bulk-import', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ pieces: importedPieces }) });
	        if (!response.ok) { const errData = await response.json(); throw new Error(errData.message || 'Failed to import inventory.'); }
	        const result = await response.json();
	        invalidateInventoryCaches();
	        alert(`Import successful! ${result.summary}`);
	        if (showInventorySummary) fetchInventory(1);
	        if (showRecentActivity) fetchGlobalLedger(1);
        setCurrentPage(1);
        setGlobalLedgerCurrentPage(1);
      } catch (err) {
        setFormError(`Import Error: ${err.message}`);
      } finally { setIsSubmitting(false); event.target.value = null; }
    };
    reader.readAsText(file);
  };

  const handleOpenAdjustmentModal = (e, piece) => {
    e.stopPropagation();
    if (!canAdjustAny) return;
    setPieceToAdjust(piece);
    setAdjustmentAmount('');
    setAdjustmentReason(defaultAdjustmentReason);
    setAdjustmentError('');
    setNewPieceIdForSplit('');
    setCorrectionDeduct('');
    setCorrectionAdd('');
    setAdjustmentRemark('');
  };

  const fetchPieceDetails = useCallback(async (skuOrGroup) => {
    setIsFetchingDetails(true);
    setDetailsError('');
    try {
      const token = await currentUser.getIdToken();

      const baseSku = typeof skuOrGroup === 'string'
        ? String(skuOrGroup || '').trim().toUpperCase()
        : String(skuOrGroup?.sku || '').trim().toUpperCase();
      const groupMemberSkus = Array.isArray(skuOrGroup?._group?.members)
        ? skuOrGroup._group.members
            .map((m) => String(m?.sku || '').trim().toUpperCase())
            .filter(Boolean)
        : [];
      const skusToFetch = [...new Set([baseSku, ...groupMemberSkus].filter(Boolean))];

      const results = await Promise.all(
        skusToFetch.map(async (sku) => {
          const response = await fetch(`/api/v1/inventory/pieces?sku=${encodeURIComponent(sku)}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (!response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errData = await response.json();
              throw new Error(errData.message || `Failed to fetch piece details for ${sku}.`);
            }
            const errorText = await response.text();
            throw new Error(`Server returned a non-JSON response for ${sku} (status: ${response.status}). Body: ${errorText.substring(0, 200)}...`);
          }
          const data = await response.json();
          const pieces = Array.isArray(data?.pieces) ? data.pieces : [];
          return pieces
            .map((piece) => ({
              ...piece,
              sku: String(piece?.sku || sku).trim().toUpperCase(),
              _sourceSku: sku,
            }))
            .filter(isDisplayableInventoryPiece);
        })
      );

      const mergedPieces = results.flat();
      setPieceDetails(mergedPieces);
    } catch (err) {
      setDetailsError(err.message);
      setPieceDetails([]); // Clear details on error
    } finally {
      setIsFetchingDetails(false);
    }
  }, [currentUser]);

  const handleSummaryRowClick = useCallback((sku) => {
    setExpandedSearchSku(null); // Collapse search results expansion
    const newExpanded = expandedSummarySku === sku ? null : sku;
    setExpandedSummarySku(newExpanded);
    if (newExpanded) {
      fetchPieceDetails(newExpanded);
    }
  }, [expandedSummarySku, fetchPieceDetails]);

  const handleSearchResultRowClick = useCallback((item) => {
    setExpandedSummarySku(null); // Collapse summary expansion
    const sku = String(item?.sku || '').trim().toUpperCase();
    const newExpanded = expandedSearchSku === sku ? null : sku;
    setExpandedSearchSku(newExpanded);
    if (newExpanded) {
      fetchPieceDetails(item);
    }
  }, [expandedSearchSku, fetchPieceDetails]);

  const handleLedgerRowClick = useCallback((entry) => {
    const newExpandedId = expandedLedgerRowId === entry.id ? null : entry.id;
    setExpandedLedgerRowId(newExpandedId);
    if (newExpandedId) {
      setExpandedSummarySku(null);
      setExpandedSearchSku(null);
      fetchPieceDetails(entry.sku);
    } else {
    }
  }, [expandedLedgerRowId, fetchPieceDetails]);

	  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingAdjustment(true);
    setAdjustmentError('');
    if (!canAdjustAny) {
      setAdjustmentError('You do not have permission to adjust inventory.');
      setIsSubmittingAdjustment(false);
      return;
    }
    if (!allowedAdjustmentReasons.some((opt) => opt.value === adjustmentReason)) {
      setAdjustmentError(`You do not have permission for '${adjustmentReason}' adjustment.`);
      setIsSubmittingAdjustment(false);
      return;
    }

    if (adjustmentReason === 'Split Piece' && !newPieceIdForSplit.trim()) {
      setAdjustmentError('A "New Piece ID" is required when splitting a piece.');
      setIsSubmittingAdjustment(false);
      return;
    }
    if (adjustmentReason === 'Use in VR Production' && !adjustmentRemark.trim()) {
      setAdjustmentError('Remark is required for "Use in VR Production".');
      setIsSubmittingAdjustment(false);
      return;
    }

    let finalAdjustmentLength = null;

    if (adjustmentReason === 'Correction') {
      const deductVal = correctionDeduct ? parseFloat(correctionDeduct) : 0;
      const addVal = correctionAdd ? parseFloat(correctionAdd) : 0;

      if ((deductVal > 0 && addVal > 0) || (deductVal === 0 && addVal === 0)) {
        setAdjustmentError('Enter either a length to deduct OR a length to add (not both).');
        setIsSubmittingAdjustment(false);
        return;
      }
      if (deductVal > 0 && deductVal > pieceToAdjust.length) {
        setAdjustmentError('Deduct length cannot exceed current piece length.');
        setIsSubmittingAdjustment(false);
        return;
      }
      // Positive for deduct, negative for add back
      finalAdjustmentLength = deductVal > 0 ? deductVal : -addVal;
    } else {
      finalAdjustmentLength = parseFloat(adjustmentAmount);
      if (adjustmentReason === 'Split Piece' && !(finalAdjustmentLength < pieceToAdjust.length)) {
        setAdjustmentError('Split amount must be strictly less than current piece length.');
        setIsSubmittingAdjustment(false);
        return;
      }
    }

    try {
      const token = await currentUser.getIdToken();

      const body = {
        sku: pieceToAdjust.sku,
        pieceId: pieceToAdjust.pieceId,
        adjustmentLength: finalAdjustmentLength,
        reason: adjustmentReason,
        remark: adjustmentRemark.trim() || null,
      };

      if (adjustmentReason === 'Split Piece') {
        body.newPieceId = newPieceIdForSplit.trim();
      }

      const response = await fetch('/api/v1/inventory/adjust', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const contentType = response.headers.get('content-type') || '';
      let data = {};
      let rawText = '';
      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        rawText = await response.text();
      }
      if (!response.ok) {
        throw new Error(
          data.message ||
          data.error ||
          rawText ||
          `Failed to adjust stock (HTTP ${response.status}).`
        );
      }

      invalidateInventoryCaches();
      alert(data.message);
      setPieceToAdjust(null); // Close modal
      // Refresh data
      if (showInventorySummary) fetchInventory(currentPage);
      if (submittedSearch) fetchSearchResults(searchCurrentPage, submittedSearch, searchSort);
      // No need to setGlobalLedgerCurrentPage(1) here as fetchGlobalLedger(1) already handles it.
      if (showRecentActivity) fetchGlobalLedger(globalLedgerCurrentPage);
    } catch (err) {
      setAdjustmentError(err.message);
    } finally {
      setIsSubmittingAdjustment(false);
    }
  };

	  const handleDeletePiece = async (e, pieceToDelete) => {
    e.stopPropagation(); // Prevent the row's onClick from firing
    if (!window.confirm(`Are you sure you want to permanently delete piece '${pieceToDelete.pieceId}' for SKU '${pieceToDelete.sku}'? This will remove its ledger entries.`)) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/inventory/pieces/force', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ sku: pieceToDelete.sku, pieceId: pieceToDelete.pieceId }),
      });

      const contentType = response.headers.get('content-type');
      const data = contentType && contentType.includes('application/json') ? await response.json() : {};
	      if (!response.ok) {
	        const errText = !contentType || !contentType.includes('application/json') ? await response.text() : '';
	        throw new Error(data.message || errText || 'Failed to delete piece.');
	      }

	      invalidateInventoryCaches();
	      alert(data.message);
	      // Remove the piece from the local state to update the UI immediately
	      setPieceDetails(prev => prev.filter(p => p.pieceId !== pieceToDelete.pieceId));
      fetchInventory(currentPage);
      // No need to setGlobalLedgerCurrentPage(1) here as fetchGlobalLedger(1) already handles it.
      fetchGlobalLedger(globalLedgerCurrentPage);

    } catch (err) {
      // Use the existing detailsError state to display the error in the modal
      setDetailsError(err.message);
    }
  };

  const handleViewPieceHistory = async (piece) => {
    setSelectedPiece(piece);
    setIsFetchingHistory(true);
    setHistoryError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/inventory/ledger-by-piece?sku=${encodeURIComponent(piece.sku)}&pieceId=${encodeURIComponent(piece.pieceId)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errData = await response.json();
          throw new Error(errData.message || 'Failed to fetch piece history.');
        } else {
          // The response is not JSON, likely an HTML error page from the server
          const errorText = await response.text();
          throw new Error(`Server returned a non-JSON response (status: ${response.status}). Body: ${errorText.substring(0, 200)}...`);
        }
      }
      const data = await response.json();
      // Convert timestamps
      const historyWithDates = data.ledger.map(entry => ({
        ...entry,
        timestamp: new Date(entry.timestamp._seconds * 1000)
      }));
      setPieceHistory(historyWithDates);
    } catch (err) {
      setHistoryError(err.message);
    } finally {
      setIsFetchingHistory(false);
    }
  };

	  const handleUndoAdjustment = async (ledgerId) => {
    if (!window.confirm('Are you sure you want to undo this adjustment? The length will be added back to the piece.')) {
      return;
    }
    setIsUndoingAdjustment(true);
    setUndoingLedgerId(ledgerId);
    setGlobalLedgerError('');
    setHistoryError(''); // Clear previous errors

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/inventory/undo-adjustment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ledgerId })
      });
	      const data = await response.json();
	      if (!response.ok) {
	        throw new Error(data.message || 'Failed to undo adjustment.');
	      }
	      invalidateInventoryCaches();
	      alert(data.message);
	      if (selectedPiece) {
	        await handleViewPieceHistory(selectedPiece);
      }
      if (showRecentActivity) await fetchGlobalLedger(globalLedgerCurrentPage);
      if (showInventorySummary) await fetchInventory(currentPage);
    } catch (err) {
      alert(`Error: ${err.message}`);
      setHistoryError(err.message);
      setGlobalLedgerError(err.message);
    } finally {
      setIsUndoingAdjustment(false);
      setUndoingLedgerId(null);
    }
  };

	  const handleDeleteLedger = async (entry) => {
    if (!entry?.id) return;
    if (!window.confirm('Delete this ledger entry and reverse its impact on inventory?')) return;
    setDeletingLedgerId(entry.id);
    setHistoryError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/inventory/ledger/${entry.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
	      const data = await response.json();
	      if (!response.ok) throw new Error(data.message || 'Failed to delete ledger entry.');
	      invalidateInventoryCaches();
	      // refresh history
	      await handleViewPieceHistory(selectedPiece);
	    } catch (err) {
      setHistoryError(err.message);
    } finally {
      setDeletingLedgerId(null);
    }
  };

  // Effect for search
  useEffect(() => {
    if (submittedSearch) {
      fetchSearchResults(searchCurrentPage, submittedSearch, searchSort);
    }
  }, [fetchSearchResults, searchCurrentPage, submittedSearch, searchSort]);

  const fetchNextPieceId = useCallback(async () => {
    const token = await currentUser.getIdToken();
    const response = await fetch(`${apiBase}/api/v1/inventory/next-piece-id`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch next piece ID.');
    }
    setPieceId(data.pieceId || '');
    return data.pieceId || '';
  }, [apiBase, currentUser]);

  const handleSelectSku = (suggestion) => {
    setSku(suggestion.sku);
    setSelectedSku(suggestion);
    setShowSkuDropdown(false);
    setSkuSuggestionError('');
    fetchNextPieceId().catch((err) => setFormError(err.message));
  };

  const isSkuValid = (() => {
    const normalized = normalizeSkuValue(sku);
    return !!normalized && /^[A-Z0-9._/-]+$/.test(normalized);
  })();

  const openAddStockModal = () => {
    setFormError('');
    setShowAddStockModal(true);
  };

  const closeAddStockModal = () => {
    if (isSubmitting) return;
    setShowAddStockModal(false);
  };

  const openSearchModal = () => setShowSearchModal(true);
  const closeSearchModal = () => {
    if (isSearching) return;
    setShowSearchModal(false);
  };

  const openBundleBuildModal = (suggestion) => {
    setSelectedBundleSuggestion(suggestion);
    setBundleBuildQty('1');
    setBundleBuildError('');
  };

  const closeBundleBuildModal = () => {
    if (isBuildingBundle) return;
    setSelectedBundleSuggestion(null);
    setBundleBuildError('');
  };

  const handleBuildBundle = async (e) => {
    e.preventDefault();
    if (!selectedBundleSuggestion) return;
    const qtyNum = Number(bundleBuildQty || 0);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      setBundleBuildError('Enter a valid positive quantity to build.');
      return;
    }
    if (qtyNum > Number(selectedBundleSuggestion.maxBuildable || 0)) {
      setBundleBuildError(`Max buildable is ${selectedBundleSuggestion.maxBuildable}.`);
      return;
    }
    setIsBuildingBundle(true);
    setBundleBuildError('');
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/inventory/bundles/build', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          bundleSku: selectedBundleSuggestion.bundleSku,
          buildQty: qtyNum,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'Failed to build bundle stock.');
      }
      invalidateInventoryCaches();
      alert(data.message || 'Bundle built successfully.');
      closeBundleBuildModal();
      fetchBundleSuggestions({ force: true });
      if (showInventorySummary) fetchInventory(currentPage);
      if (submittedSearch) fetchSearchResults(searchCurrentPage, submittedSearch, searchSort);
      if (showRecentActivity) fetchGlobalLedger(globalLedgerCurrentPage, { force: true });
    } catch (err) {
      setBundleBuildError(err.message || 'Failed to build bundle stock.');
    } finally {
      setIsBuildingBundle(false);
    }
  };

  const fetchBundleComponentPieces = useCallback(async (skuUpper) => {
    if (!skuUpper || !currentUser) return;
    const sku = String(skuUpper).trim().toUpperCase();
    if (!sku) return;
    if (bundleComponentPieces[sku]) return;

    setBundleComponentLoading((prev) => ({ ...prev, [sku]: true }));
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/inventory/pieces?sku=${encodeURIComponent(sku)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || `Failed to fetch pieces for ${sku}.`);
      }
      const data = await response.json();
      const pieces = (Array.isArray(data?.pieces) ? data.pieces : []).filter(isDisplayableInventoryPiece);
      setBundleComponentPieces((prev) => ({ ...prev, [sku]: pieces }));
    } catch (err) {
      setBundleComponentPieces((prev) => ({ ...prev, [sku]: [] }));
      setBundleSuggestionsError(err.message || 'Failed to fetch component pieces.');
    } finally {
      setBundleComponentLoading((prev) => ({ ...prev, [sku]: false }));
    }
  }, [bundleComponentPieces, currentUser]);

  const toggleBundleComponentExpansion = async (bundleSku, compSku) => {
    const key = `${String(bundleSku || '').toUpperCase()}::${String(compSku || '').toUpperCase()}`;
    const nextOpen = !expandedBundleComponents[key];
    setExpandedBundleComponents((prev) => ({ ...prev, [key]: nextOpen }));
    if (nextOpen) {
      await fetchBundleComponentPieces(compSku);
    }
  };

  const formatMaybeTimestamp = (value) => {
    if (!value) return '-';
    if (typeof value?.toDate === 'function') {
      try {
        return value.toDate().toLocaleString();
      } catch (_) {
        return '-';
      }
    }
    if (typeof value === 'object' && value?._seconds) {
      return new Date(Number(value._seconds) * 1000).toLocaleString();
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString();
  };

  const formatReasonOrderText = (entry) => {
    const type = String(entry?.type || '').toUpperCase();
    const reason = String(entry?.reason || '').trim();
    const batchId = String(entry?.batchId || '').trim();
    if (type === 'BATCH_RECEIVE') {
      if (reason) return reason;
      return batchId ? `Excess fabric from batch ${batchId} restocked.` : 'Excess fabric from batch restocked.';
    }
    const orderRaw = String(entry?.orderNumber || '').trim();
    if ((type === 'SALE' || type === 'SALE_UNDO') && orderRaw) {
      return orderRaw;
    }
    const orderText = orderRaw
      ? (() => {
          const isNumeric = /^[0-9]+$/.test(orderRaw);
          return `#${isNumeric ? `${orderRaw}00` : orderRaw}`;
        })()
      : '';
    if (reason && orderText) return `${reason} • ${orderText}`;
    if (reason) return reason;
    if (orderText) return orderText;
    return 'N/A';
  };

  const filteredRecentLedger = (() => {
    const type = (entry) => String(entry?.type || '').toUpperCase();
    if (recentActivityFilter === 'new') return globalLedger.filter((e) => type(e) === 'NEW_STOCK');
    if (recentActivityFilter === 'used') return globalLedger.filter((e) => type(e) === 'SALE');
    if (recentActivityFilter === 'restock') return globalLedger.filter((e) => type(e) === 'RETURN' || type(e) === 'RESTOCK');
    if (recentActivityFilter === 'batchReceived') return globalLedger.filter((e) => type(e) === 'BATCH_RECEIVE');
    if (recentActivityFilter === 'adjustment') return globalLedger.filter((e) => type(e) === 'ADJUSTMENT');
    if (recentActivityFilter === 'correction') return globalLedger.filter((e) => type(e) === 'CORRECTION');
    return globalLedger;
  })();
  const displayedSearchResults =
    Array.isArray(groupedSearchResults) &&
    (
      groupedSearchResults.length > 0 ||
      (Array.isArray(searchResults) && searchResults.length === 0)
    )
      ? groupedSearchResults
      : (Array.isArray(searchResults) ? searchResults : []);

  const toggleSearchSort = (key) => {
    setSearchSort((prev) => {
      const cur = prev || { key: 'sku', dir: 'asc' };
      if (cur.key === key) {
        return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
    // Sorting is applied server-side (sort-before-pagination), so jump back to page 1.
    setSearchCurrentPage(1);
  };

  const sortedSearchResults = React.useMemo(() => {
    const list = Array.isArray(displayedSearchResults) ? [...displayedSearchResults] : [];
    const key = searchSort?.key || 'sku';
    const dir = searchSort?.dir === 'desc' ? -1 : 1;
    list.sort((a, b) => {
      if (key === 'totalLength') {
        const diff = Number(a?.totalLength || 0) - Number(b?.totalLength || 0);
        if (diff !== 0) return diff * dir;
      } else if (key === 'pieceCount') {
        const diff = Number(a?.pieceCount || 0) - Number(b?.pieceCount || 0);
        if (diff !== 0) return diff * dir;
      }
      const as = String(a?.sku || '').toUpperCase();
      const bs = String(b?.sku || '').toUpperCase();
      return as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
    return list;
  }, [displayedSearchResults, searchSort]);

  const fetchImageForSku = useCallback(async (skuUpper) => {
    if (!currentUser || !skuUpper) return '';
    const token = await currentUser.getIdToken();
    const response = await fetch(`/api/v1/products?page=1&limit=25&search=${encodeURIComponent(skuUpper)}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) return '';
    const data = await response.json().catch(() => ({}));
    const products = Array.isArray(data?.products) ? data.products : [];
    for (const product of products) {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      for (const variant of variants) {
        const vSku = String(variant?.sku || '').trim().toUpperCase();
        if (!vSku || vSku !== skuUpper) continue;
        const images = Array.isArray(product?.images) ? product.images : [];
        const variantImage = images.find((img) => img?.id === variant?.image_id);
        return String(variantImage?.src || product?.image?.src || '').trim();
      }
    }
    return '';
  }, [currentUser]);

  useEffect(() => {
    if (!showSearchModal) return;
    if (!Array.isArray(sortedSearchResults) || sortedSearchResults.length === 0) return;
    let cancelled = false;

    const run = async () => {
      const skus = sortedSearchResults
        .map((r) => String(r?.sku || '').trim().toUpperCase())
        .filter(Boolean)
        .slice(0, 25);
      for (const skuUpper of skus) {
        if (cancelled) return;
        if (Object.prototype.hasOwnProperty.call(searchSkuImageMap, skuUpper)) continue;
        try {
          const src = await fetchImageForSku(skuUpper);
          if (cancelled) return;
          setSearchSkuImageMap((prev) => ({ ...prev, [skuUpper]: src || '' }));
        } catch (_) {
          if (cancelled) return;
          setSearchSkuImageMap((prev) => ({ ...prev, [skuUpper]: '' }));
        }
      }
    };

    run();
    return () => { cancelled = true; };
    // Intentionally omit searchSkuImageMap to avoid refetching already-cached keys on every setState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearchModal, sortedSearchResults, fetchImageForSku]);
  const selectedDraftSku = normalizeSkuValue(skuInputValue);
  const hasSelectedDraftSku =
    !!selectedDraftSku &&
    /^[A-Z0-9._/-]+$/.test(selectedDraftSku);

  return (
    <div className="inventory-container">
      <div className="inventory-nav-tabs-wrapper">
        <div className="inventory-nav-tabs">
          <button
            type="button"
            className={`inventory-nav-tab ${activeTab === 'inventory' ? 'active' : ''}`}
            onClick={() => setActiveTab('inventory')}
          >
            📦 Inventory & Ledger
          </button>
          {(canPerformAction('inventory.audit') || canPerformAction('inventory.adjustStock') || currentUser?.admin) && (
            <button
              type="button"
              className={`inventory-nav-tab ${activeTab === 'stock_audit' ? 'active' : ''}`}
              onClick={() => setActiveTab('stock_audit')}
            >
              📋 Stock Audit
            </button>
          )}
        </div>
      </div>

      {activeTab === 'stock_audit' ? (
        <StockAuditTabView
          apiBase={apiBase}
          onAuditSaved={() => {
            invalidateInventoryCaches();
            if (showInventorySummary) fetchInventory(currentPage);
            if (showRecentActivity) fetchGlobalLedger(1);
          }}
        />
      ) : (
        <>
          <div className="page-header">
            <h1>Inventory Management</h1>
            <div className="header-actions">
                {canPerformAction('inventory.addStock') && (
                  <button onClick={openAddStockModal} className="add-stock-btn">
                    Add New Stock
                  </button>
                )}
                <button onClick={openSearchModal} className="search-open-btn">
                  Search
                </button>
                <button onClick={() => setShowSocialPhotographyModal(true)} className="social-photography-open-btn">
                  Social Photography
                </button>
                {canPerformAction('inventory.printLabels') && (
                  <button onClick={() => setShowLabelPrintModal(true)} className="print-labels-btn">
                    Print Labels
                  </button>
                )}
                {(canPerformAction('inventory.audit') || canPerformAction('inventory.adjustStock') || currentUser?.admin) && (
                  <button
                    onClick={() => setActiveTab('stock_audit')}
                    className="action-button stock-audit-btn"
                    style={{ background: '#0284c7', color: '#fff', fontWeight: '600' }}
                  >
                    📋 Stock Audit
                  </button>
                )}
                {canPerformAction('inventory.exportStock') && <button onClick={handleExportCSV} className="export-btn">Export CSV</button>}
                
                {isCheckingImport ? (
                  <button className="import-btn" disabled>Checking...</button>
                ) : lastImportId ? (
                  SHOW_UNDO_IMPORT_BUTTON && canPerformAction('inventory.undoImport') && (
                    <button onClick={handleUndoImport} className="undo-btn" disabled={isUndoing}>
                        {isUndoing ? 'Undoing...' : 'Undo Last Import'}
                    </button>
                  )
            ) : (
              canPerformAction('inventory.importStock') && (
                <>
                  <label htmlFor="csv-import-input" className="import-btn">
                    Import Opening Stock
                  </label>
                  <input id="csv-import-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} disabled={isSubmitting} />
                </>
              )
            )}
            {SHOW_PURGE_USAGE_BUTTON && canPerformAction('inventory.undoImport') && (
              <button onClick={handlePurgeUsage} className="danger-btn" disabled={isPurgingUsage}>
                {isPurgingUsage ? 'Purging...' : 'Delete Used/Correction/Restock'}
              </button>
            )}
        </div>
      </div>

      <LabelPrintModal
        isOpen={showLabelPrintModal}
        onClose={() => setShowLabelPrintModal(false)}
        apiBase={apiBase}
      />

      <SocialPhotographyModal
        isOpen={showSocialPhotographyModal}
        onClose={() => setShowSocialPhotographyModal(false)}
        onInventoryChanged={() => {
          invalidateInventoryCaches();
          if (showInventorySummary) fetchInventory(currentPage);
          if (showRecentActivity) fetchGlobalLedger(1, { force: true });
        }}
      />

      <StockAuditModal
        isOpen={showStockAuditModal}
        onClose={() => {
          setShowStockAuditModal(false);
          invalidateInventoryCaches();
          if (showInventorySummary) fetchInventory(currentPage);
          if (showRecentActivity) fetchGlobalLedger(1);
        }}
        apiBase={apiBase}
      />

      <div className="inventory-section">
        <button
          type="button"
          className="inventory-accordion-toggle"
          onClick={() => setShowBundleSuggestions((v) => !v)}
          aria-expanded={showBundleSuggestions}
          aria-controls="bundle-suggestions-accordion-content"
        >
          <span className="inventory-accordion-title">
            Bundle Build Suggestions
            <span className="bundle-suggestions-count">{bundleSuggestions.length}</span>
          </span>
          <span className={`inventory-accordion-chevron ${showBundleSuggestions ? 'open' : ''}`}>▾</span>
        </button>
        {showBundleSuggestions && (
          <div id="bundle-suggestions-accordion-content" className="sku-group-accordion-content">
            <div className="sku-group-accordion-actions">
              <button
                className="action-button toggle-visibility-btn"
                onClick={() => fetchBundleSuggestions({ force: true })}
                disabled={isLoadingBundleSuggestions}
              >
                {isLoadingBundleSuggestions ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            {bundleSuggestionsError && <p className="error-message">Error: {bundleSuggestionsError}</p>}
            {isLoadingBundleSuggestions && bundleSuggestions.length === 0 && <p>Loading suggestions...</p>}
            {!isLoadingBundleSuggestions && bundleSuggestions.length === 0 && !bundleSuggestionsError && (
              <p>No buildable bundles from current stock.</p>
            )}
            {bundleSuggestions.length > 0 && (
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Bundle SKU</th>
                    <th>Max Buildable</th>
                    <th>Components</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bundleSuggestions.map((s) => (
                    <tr key={s.bundleSku}>
                      <td>
                        {s.imageUrl ? (
                          <img src={s.imageUrl} alt={s.bundleSku} className="bundle-suggestion-image" />
                        ) : (
                          <div className="bundle-suggestion-image placeholder">No Image</div>
                        )}
                      </td>
                      <td>{s.bundleSku}</td>
                      <td>{s.maxBuildable}</td>
                  <td>
                    <div className="bundle-components-list">
                      {(s.components || []).map((c) => {
                        const compSku = String(c.sku || '').toUpperCase();
                        const compKey = `${String(s.bundleSku || '').toUpperCase()}::${compSku}`;
                        const isOpen = !!expandedBundleComponents[compKey];
                        const isLoading = !!bundleComponentLoading[compSku];
                        const pieces = bundleComponentPieces[compSku] || [];
                        return (
                          <div key={`${s.bundleSku}-${c.sku}`} className="bundle-component-row">
                            <div className="bundle-component-line">
                              <span>{c.sku} ({c.perBundleQty}/bundle, stock {Number(c.totalAvailable || 0).toFixed(2)})</span>
                              <button
                                type="button"
                                className="bundle-component-toggle"
                                onClick={() => toggleBundleComponentExpansion(s.bundleSku, compSku)}
                              >
                                {isOpen ? 'Hide pieces' : 'Show pieces'}
                              </button>
                            </div>
                            {isOpen && (
                              <div className="bundle-component-pieces">
                                {isLoading && <span className="bundle-component-loading">Loading...</span>}
                                {!isLoading && pieces.length === 0 && (
                                  <span className="bundle-component-empty">No active pieces</span>
                                )}
                                {!isLoading && pieces.length > 0 && pieces.map((p) => (
                                  <button
                                    type="button"
                                    key={`${compSku}-${p.pieceId}`}
                                    className="bundle-piece-pill"
                                    onClick={() => handleViewPieceHistory({ sku: compSku, pieceId: p.pieceId })}
                                  >
                                    {p.pieceId} • {Number(p.length || 0).toFixed(2)}m
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                      <td>
                        <button
                          className="action-button adjust-action-btn"
                          onClick={() => openBundleBuildModal(s)}
                          disabled={!canPerformAction('inventory.addStock')}
                        >
                          Build
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {canViewQuarantineQueue && (
        <div className="inventory-section">
          <button
            type="button"
            className="inventory-accordion-toggle"
            onClick={() => setShowQuarantineQueue((prev) => !prev)}
            aria-expanded={showQuarantineQueue}
            aria-controls="quarantine-queue-accordion-content"
          >
            <span className="inventory-accordion-title">
              Quarantine Queue
              <span className="bundle-suggestions-count">{quarantineQueue.length}</span>
            </span>
            <span className={`inventory-accordion-chevron ${showQuarantineQueue ? 'open' : ''}`}>▾</span>
          </button>
          {showQuarantineQueue && (
            <div id="quarantine-queue-accordion-content" className="sku-group-accordion-content">
              <div className="sku-group-accordion-actions">
                <button
                  className="action-button toggle-visibility-btn"
                  onClick={() => fetchQuarantineQueue({ force: true, includeResolved: includeResolvedQuarantine })}
                  disabled={isLoadingQuarantineQueue}
                >
                  {isLoadingQuarantineQueue ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  className="action-button toggle-visibility-btn"
                  onClick={() => setIncludeResolvedQuarantine((prev) => !prev)}
                >
                  {includeResolvedQuarantine ? 'Open Only' : 'Include Resolved'}
                </button>
              </div>
              {quarantineQueueError && <p className="error-message">{quarantineQueueError}</p>}
              {isLoadingQuarantineQueue && quarantineQueue.length === 0 && <p>Loading quarantine queue...</p>}
              {!isLoadingQuarantineQueue && quarantineQueue.length === 0 && !quarantineQueueError && (
                <p>No quarantine requests found.</p>
              )}
              {quarantineQueue.length > 0 && (
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>Updated</th>
                      <th>Order</th>
                      <th>SKU</th>
                      <th>Qty (m)</th>
                      <th>Status</th>
                      <th>Piece</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quarantineQueue.map((row) => {
                      const queueId = row.queueId || row.id;
                      const pendingLabel = quarantineActionById[queueId] || '';
                      const statusUpper = String(row.status || '').toUpperCase();
                      const isOpen = statusUpper === 'OPEN';
                      const isResolved = statusUpper === 'RESOLVED';
                      const isQuarantined = String(row.quarantineState || '').toUpperCase() === 'QUARANTINED';
                      return (
                        <tr key={queueId}>
                          <td>{formatMaybeTimestamp(row.updatedAt || row.createdAt)}</td>
                          <td>{row.orderNumber || row.orderId || '-'}</td>
                          <td>{row.sku || '-'}</td>
                          <td>{Number(row.qty || 0).toFixed(2)}</td>
                          <td>{row.status || '-'}</td>
                          <td>{row.quarantinedPieceId || row.candidatePieceId || '-'}</td>
                          <td className="actions-cell">
                            {canActOnQuarantineQueue && isOpen && !isQuarantined && (
                              <button
                                className="action-button adjust-action-btn"
                                onClick={() =>
                                  runQuarantineQueueAction(
                                    queueId,
                                    'quarantine-piece',
                                    {},
                                    'Quarantining...'
                                  )
                                }
                                disabled={!!pendingLabel}
                              >
                                {pendingLabel || 'Quarantine Piece'}
                              </button>
                            )}
                            {canActOnQuarantineQueue && isOpen && isQuarantined && (
                              <>
                                <button
                                  className="action-button undo-action-btn"
                                  onClick={() =>
                                    runQuarantineQueueAction(
                                      queueId,
                                      'release-piece',
                                      {},
                                      'Releasing...'
                                    )
                                  }
                                  disabled={!!pendingLabel}
                                >
                                  {pendingLabel || 'Release Piece'}
                                </button>
                                <button
                                  className="action-button delete-action-btn"
                                  onClick={() =>
                                    runQuarantineQueueAction(
                                      queueId,
                                      'resolve',
                                      { resolution: 'WRITE_OFF' },
                                      'Writing off...'
                                    )
                                  }
                                  disabled={!!pendingLabel}
                                >
                                  {pendingLabel || 'Write Off'}
                                </button>
                              </>
                            )}
                            {canActOnQuarantineQueue && isResolved && (
                              <button
                                className="action-button undo-action-btn"
                                onClick={() =>
                                  runQuarantineQueueAction(
                                    queueId,
                                    'undo-resolve',
                                    {},
                                    'Reopening...'
                                  )
                                }
                                disabled={!!pendingLabel}
                              >
                                {pendingLabel || 'Undo Resolve'}
                              </button>
                            )}
                            {!canActOnQuarantineQueue && <span>-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {canPerformAction('inventory.addStock') && (
        <Modal isOpen={showAddStockModal} onClose={closeAddStockModal} contentClassName="inventory-add-stock-modal">
          <div className="inventory-add-stock-modal-header">
            <h2>Add New Stock</h2>
            <p className="section-subtitle">Search a product SKU, review details, and add a tagged piece in one go.</p>
          </div>
          {formError && <div className="form-error">{formError}</div>}
          <form onSubmit={handleAddStock} className="add-stock-form">
            <div className="form-row">
              <div className="form-group sku-autocomplete">
                <label>Select SKU</label>
                <div className="sku-input-wrapper">
                  <input
                    type="text"
                    value={sku}
                    onChange={(e) => {
                      const value = normalizeSkuValue(e.target.value);
                      setSku(value);
                      if (!value || selectedSku?.sku !== value) {
                        setSelectedSku(null);
                      }
                      setShowSkuDropdown(true);
                      setSkuSuggestionError('');
                    }}
                    onFocus={() => setShowSkuDropdown(true)}
                    required
                    placeholder="Type SKU (existing or new)"
                  />
                  {isFetchingSkuSuggestions && <span className="sku-searching">Searching...</span>}
                </div>
                {showSkuDropdown && (
                  <div className="sku-suggestions">
                    {skuSuggestionError && skuSuggestions.length === 0 && (
                      <div className="sku-suggestion-message">{skuSuggestionError}</div>
                    )}
                    {skuSuggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={suggestion.sku}
                        className={`sku-suggestion-item ${selectedSku?.sku === suggestion.sku ? 'active' : ''}`}
                        onMouseDown={() => handleSelectSku(suggestion)}
                      >
                        {suggestion.imageSrc ? (
                          <img src={suggestion.imageSrc} alt={suggestion.productTitle} className="sku-suggestion-thumb" />
                        ) : (
                          <div className="sku-suggestion-thumb placeholder">No Image</div>
                        )}
                        <div className="sku-suggestion-text">
                          <span className="sku-suggestion-sku">{suggestion.sku}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {selectedSku && (
                  <div className="selected-sku-pill">
                    {selectedSku.imageSrc ? (
                      <img src={selectedSku.imageSrc} alt={selectedSku.productTitle} className="sku-pill-thumb" />
                    ) : (
                      <div className="sku-pill-thumb placeholder">No Image</div>
                    )}
                    <div className="selected-sku-text">
                      <span className="selected-sku-sku">{selectedSku.sku}</span>
                    </div>
                    <button type="button" className="clear-sku-btn" onClick={() => { setSelectedSku(null); setSku(''); setSkuSuggestions([]); }}>×</button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Piece ID / Bin Number</label>
                <input
                  type="text"
                  value={pieceId}
                  onChange={(e) => setPieceId(e.target.value)}
                  required
                  disabled={!isSkuValid}
                  placeholder={isSkuValid ? 'e.g., BIN1' : 'Enter valid SKU first'}
                />
              </div>
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  step="0.25"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  required
                  disabled={!isSkuValid}
                  placeholder={isSkuValid ? 'e.g., 5.0' : 'Enter valid SKU first'}
                />
              </div>
              <div className="form-group">
                <label>Remark (optional)</label>
                <input
                  type="text"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  disabled={!isSkuValid}
                  placeholder={isSkuValid ? 'Notes or context' : 'Enter valid SKU first'}
                />
              </div>
            </div>
            <div className="add-stock-actions">
              <button type="submit" disabled={isSubmitting || !isSkuValid}>
                {isSubmitting ? 'Adding...' : 'Add to Inventory'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      <Modal isOpen={showSearchModal} onClose={closeSearchModal} contentClassName="inventory-search-modal">
        <div className="inventory-filter-bar search-card">
          <div className="search-filter">
            <div className="search-heading">
              <div className="search-title">Search Inventory</div>
              <div className="search-subtitle">Type part of a SKU to quickly filter results.</div>
            </div>
            <div className="search-input-row">
              <div className="search-input-wrapper">
                <input
                  id="inventory-search"
                  type="text"
                  placeholder="e.g., PA10630 or JS-0"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    const trimmed = String(searchInput || '').trim();
                    if (trimmed.length < 3) return;
                    handleSearchSubmit();
                  }}
                />
                {searchInput && <button onClick={handleClearSearch} className="clear-search-btn">&times;</button>}
                {isSearching && <span className="search-loading-indicator">Searching...</span>}
              </div>
              <button
                onClick={handleSearchSubmit}
                className="search-btn"
                disabled={String(searchInput || '').trim().length < 3}
                title={String(searchInput || '').trim().length < 3 ? 'Type at least 3 characters to search' : 'Search'}
              >
                Search
              </button>
            </div>
          </div>
        </div>
        {isSearching && <p>Searching...</p>}
        {error && !isSearching && searchResults !== null && <p className="error-message">Error: {error}</p>}
        {isGroupingSearch && <p>Calculating grouped stock...</p>}
        {groupedSearchError && <p className="error-message">{groupedSearchError}</p>}
        {searchResults !== null && !isSearching && !error && (
          <>
            <p className="search-results-count">
              {sortedSearchResults.length} result(s) shown for "{submittedSearch}"
            </p>
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>
                    <button type="button" className="inventory-sort-btn" onClick={() => toggleSearchSort('sku')}>
                      SKU
                      {searchSort?.key === 'sku' && (
                        <span className="inventory-sort-arrow">{searchSort?.dir === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="inventory-sort-btn" onClick={() => toggleSearchSort('totalLength')}>
                      Qty (m)
                      {searchSort?.key === 'totalLength' && (
                        <span className="inventory-sort-arrow">{searchSort?.dir === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  </th>
                  <th>Pieces</th>
                </tr>
              </thead>
              <tbody>
                {sortedSearchResults.length > 0 ? (
                  sortedSearchResults.map(item => (
                    <React.Fragment key={item.sku}>
                      <tr onClick={() => handleSearchResultRowClick(item)} className={`inventory-row-clickable ${expandedSearchSku === item.sku ? 'expanded' : ''}`}>
                        <td className="inventory-thumb-cell">
                          {searchSkuImageMap[String(item?.sku || '').trim().toUpperCase()] ? (
                            <img
                              src={searchSkuImageMap[String(item?.sku || '').trim().toUpperCase()]}
                              alt={item.sku}
                              className="inventory-sku-thumb"
                              loading="lazy"
                            />
                          ) : (
                            <div className="inventory-sku-thumb placeholder">No Image</div>
                          )}
                        </td>
                        <td className="sku-clickable">
                          {item.sku}
                          {item?._group?.members?.length > 0 && (
                            <>
                              <span className="grouped-stock-badge">Grouped Stock</span>
                              <div className="grouped-members-note">
                                Group: {(item._group.name || item._group.groupId || 'SKU Group')}
                              </div>
                            </>
                          )}
                        </td>
                        <td>{item.totalLength.toFixed(2)}</td>
                        <td>{displayPieceCount(item)}</td>
                      </tr>
                      {item?._group?.members?.length > 0 && (
                        <tr className="grouped-members-row">
                          <td colSpan="4">
                            {item._group.members.map((m) => (
                              <span key={`${item.sku}-${m.sku}`} className="grouped-member-chip">
                                {m.sku}: {Number(m.totalLength || 0).toFixed(2)}m ({displayPieceCount(m)} pcs)
                              </span>
                            ))}
                          </td>
                        </tr>
                      )}
                      {expandedSearchSku === item.sku && (
                        <tr className="details-row">
                          <td colSpan="4">
                            <div className="details-content">
                              {isFetchingDetails && <p>Loading details...</p>}
                              {detailsError && <p className="error-message">{detailsError}</p>}
                              {!isFetchingDetails && !detailsError && (
                                <table className="inventory-table">
                                  <thead>
                                    <tr>
                                      <th>Piece ID / Bin</th>
                                      <th>Available Length (m)</th>
                                      {(canAdjustAny || canPerformAction('inventory.deletePiece')) && <th>Actions</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pieceDetails.length > 0 ? (
                                      pieceDetails.map(piece => (
                                        <tr key={`${piece._sourceSku || piece.sku || 'SKU'}:${piece.pieceId}`}>
                                          <td onClick={() => handleViewPieceHistory(piece)} className="sku-clickable">
                                            {piece.pieceId}
                                            {piece?._sourceSku && (
                                              <div className="grouped-members-note">SKU: {piece._sourceSku}</div>
                                            )}
                                          </td>
                                          <td>{piece.length.toFixed(2)}</td>
                                          {(canAdjustAny || canPerformAction('inventory.deletePiece')) && (
                                            <td className="actions-cell">
                                              {canAdjustAny && (
                                                <button onClick={(e) => handleOpenAdjustmentModal(e, piece)} className="action-button adjust-action-btn" title="Adjust Stock">Adjust</button>
                                              )}
                                              {canPerformAction('inventory.deletePiece') && (<button onClick={(e) => handleDeletePiece(e, piece)} className="action-button delete-action-btn" title="Delete Piece">
                                                  Delete
                                              </button>)}
                                            </td>
                                          )}
                                        </tr>
                                      ))
                                    ) : (
                                      <tr><td colSpan="3">No individual pieces found for this SKU.</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr><td colSpan="4">No inventory found for "{submittedSearch}".</td></tr>
                )}
              </tbody>
            </table>
            <Pagination
              currentPage={searchCurrentPage}
              totalItems={totalSearchResults}
              itemsPerPage={itemsPerPage}
              onPageChange={handleSearchPageChange}
            />
          </>
        )}
      </Modal>

      <Modal isOpen={!!selectedBundleSuggestion} onClose={closeBundleBuildModal} contentClassName="inventory-add-stock-modal">
        <div className="inventory-add-stock-modal-header">
          <h2>Build Bundle Stock</h2>
          <p className="section-subtitle">Consume component stock and create bundle SKU stock.</p>
        </div>
        {bundleBuildError && <div className="form-error">{bundleBuildError}</div>}
        {selectedBundleSuggestion && (
          <form onSubmit={handleBuildBundle} className="add-stock-form">
            <div className="form-row">
              <div className="form-group">
                <label>Bundle SKU</label>
                <input type="text" value={selectedBundleSuggestion.bundleSku} readOnly />
              </div>
              <div className="form-group">
                <label>Max Buildable</label>
                <input type="text" value={selectedBundleSuggestion.maxBuildable} readOnly />
              </div>
              <div className="form-group">
                <label>Build Quantity</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={bundleBuildQty}
                  onChange={(e) => setBundleBuildQty(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Component Consumption</label>
                <div className="bundle-components-preview">
                  {(selectedBundleSuggestion.components || []).map((c) => {
                    const qty = Number(bundleBuildQty || 0) * Number(c.perBundleQty || 0);
                    return (
                      <div key={c.sku}>
                        {c.sku}: {Number.isFinite(qty) ? qty.toFixed(2) : '0.00'}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="add-stock-actions">
              <button type="button" className="cancel-btn" onClick={closeBundleBuildModal} disabled={isBuildingBundle}>
                Cancel
              </button>
              <button type="submit" disabled={isBuildingBundle}>
                {isBuildingBundle ? 'Building...' : 'Confirm Build'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {SHOW_INVENTORY_SUMMARY_SECTION && (
        <div className="inventory-section">
          <div className="section-header">
            <h2>Inventory Summary</h2>
            <button
              onClick={() => {
                if (!showInventorySummary) fetchInventory(1);
                setShowInventorySummary(!showInventorySummary);
              }}
              className="action-button toggle-visibility-btn"
            >
              {showInventorySummary ? 'Hide Summary' : 'Show Summary'}
            </button>
          </div>
          {showInventorySummary && (
            <>
              {isLoading && <p>Loading inventory...</p>}
              {error && <p className="error-message">Error: {error}</p>}
              {!isLoading && !error && (
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Total Available Length (m)</th>
                      <th>Number of Pieces</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventory.length > 0 ? (
                      inventory.map(item => (
                        <React.Fragment key={item.sku}>
                          <tr
                            onClick={() => handleSummaryRowClick(item.sku)}
                            className={`inventory-row-clickable ${expandedSummarySku === item.sku ? 'expanded' : ''}`}
                          >
                            <td className="sku-clickable">{item.sku}</td>
                            <td>{item.totalLength.toFixed(2)}</td>
                            <td>{displayPieceCount(item)}</td>
                          </tr>
                          {expandedSummarySku === item.sku && (
                            <tr className="details-row">
                              <td colSpan="3">
                                <div className="details-content">
                                  {isFetchingDetails && <p>Loading details...</p>}
                                  {detailsError && <p className="error-message">{detailsError}</p>}
                                  {!isFetchingDetails && !detailsError && (
                                    <table className="inventory-table">
                                      <thead>
                                        <tr>
                                          <th>Piece ID / Bin</th>
                                          <th>Available Length (m)</th>
                                          {(canAdjustAny || canPerformAction('inventory.deletePiece')) && (
                                            <th>Actions</th>
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {pieceDetails.length > 0 ? (
                                          pieceDetails.map(piece => (
                                            <tr key={piece.pieceId}>
                                              <td onClick={() => handleViewPieceHistory(piece)} className="sku-clickable">
                                                {piece.pieceId}
                                              </td>
                                              <td>{piece.length.toFixed(2)}</td>
                                              {(canAdjustAny || canPerformAction('inventory.deletePiece')) && (
                                                <td className="actions-cell">
                                                  {canAdjustAny && (
                                                    <button
                                                      onClick={(e) => handleOpenAdjustmentModal(e, piece)}
                                                      className="action-button adjust-action-btn"
                                                      title="Adjust Stock"
                                                    >
                                                      Adjust
                                                    </button>
                                                  )}
                                                  {canPerformAction('inventory.deletePiece') && (
                                                    <button
                                                      onClick={(e) => handleDeletePiece(e, piece)}
                                                      className="action-button delete-action-btn"
                                                      title="Delete Piece"
                                                    >
                                                      Delete
                                                    </button>
                                                  )}
                                                </td>
                                              )}
                                            </tr>
                                          ))
                                        ) : (
                                          <tr><td colSpan="3">No individual pieces found for this SKU.</td></tr>
                                        )}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3">No inventory found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
      )}

      <div className="inventory-section">
        <div className="section-header">
          <h2>Recent Activity</h2>
          <div className="section-actions">
            <CacheStatusBar
              cacheKey={globalLedgerCacheKey}
              onRefresh={() => fetchGlobalLedger(globalLedgerCurrentPage, { force: true })}
              isRefreshing={isFetchingGlobalLedger}
              hideStaleTag
              className="inventory-cache-status"
            />
            {canPerformAction('inventory.exportLedger') && (
              <button onClick={handleExportActivityCSV} className="export-btn">
                Export CSV
              </button>
            )}
          </div>
        </div>
        <div className="activity-filters" role="tablist" aria-label="Recent activity filters">
          {[
            { key: 'all', label: 'All' },
            { key: 'new', label: 'New' },
            { key: 'used', label: 'Used' },
            { key: 'restock', label: 'Restock' },
            { key: 'batchReceived', label: 'Batch Received' },
            { key: 'adjustment', label: 'Adjustment' },
            { key: 'correction', label: 'Correction' },
          ].map((f) => (
            <button
              key={f.key}
              className={`activity-filter-pill ${recentActivityFilter === f.key ? 'active' : ''}`}
              onClick={() => setRecentActivityFilter(f.key)}
              aria-pressed={recentActivityFilter === f.key}
            >
              {f.label}
            </button>
          ))}
        </div>
        {showRecentActivity && (
          <>
            {isFetchingGlobalLedger && <p>Loading history...</p>}
            {globalLedgerError && <p className="error-message">{globalLedgerError}</p>}
            {!isFetchingGlobalLedger && !globalLedgerError && (
              <>
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>SKU</th>
                  <th>Piece ID</th>
                  <th>Type</th>
                  <th>Change (m)</th>
                  <th>Reason/Order #</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecentLedger.length > 0 ? (
                  filteredRecentLedger.map((entry) => (
                    <React.Fragment key={entry.id}>
                      <tr>
                      <td>{entry.timestamp.toLocaleString()}</td>
                        <td onClick={() => handleLedgerRowClick(entry)} className="sku-clickable">{entry.sku}</td>
                      <td>{entry.pieceId}</td>
                      <td><span className={`ledger-type-tag ${entry.type.toLowerCase()}`}>{entry.type}</span></td>
                      <td className={entry.change > 0 ? 'positive-change' : 'negative-change'}>{entry.change.toFixed(2)}m</td>
                      <td>{formatReasonOrderText(entry)}</td>
                      <td>
                        {entry.type === 'ADJUSTMENT' && canPerformAction('inventory.undoAdjustment') && (
                          <button
                            onClick={() => handleUndoAdjustment(entry.id)}
                            disabled={isUndoingAdjustment && undoingLedgerId === entry.id}
                            className="action-button undo-action-btn"
                          >
                            {isUndoingAdjustment && undoingLedgerId === entry.id ? 'Undoing...' : 'Undo'}
                          </button>
                        )}
                      </td>
                      </tr>
                      {expandedLedgerRowId === entry.id && (
                        <tr className="details-row">
                          <td colSpan="7">
                            <div className="details-content">
                              {isFetchingDetails && <p>Loading details...</p>}
                              {detailsError && <p className="error-message">{detailsError}</p>}
                              {!isFetchingDetails && !detailsError && (
                                <table className="inventory-table">
                                  <thead>
                                    <tr>
                                      <th>Piece ID / Bin</th>
                                      <th>Available Length (m)</th>
                                      {(canAdjustAny || canPerformAction('inventory.deletePiece')) && <th>Actions</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pieceDetails.length > 0 ? (
                                      pieceDetails.map(piece => (
                                        <tr key={piece.pieceId}>
                                          <td onClick={() => handleViewPieceHistory(piece)} className="sku-clickable">{piece.pieceId}</td>
                                          <td>{piece.length.toFixed(2)}</td>
                                          {(canAdjustAny || canPerformAction('inventory.deletePiece')) && (
                                            <td className="actions-cell">
                                              {canAdjustAny && (
                                                <button onClick={(e) => handleOpenAdjustmentModal(e, piece)} className="action-button adjust-action-btn" title="Adjust Stock">Adjust</button>
                                              )}
                                              {canPerformAction('inventory.deletePiece') && (<button onClick={(e) => handleDeletePiece(e, piece)} className="action-button delete-action-btn" title="Delete Piece">
                                                  Delete
                                              </button>)}
                                            </td>
                                          )}
                                        </tr>
                                      ))
                                    ) : (
                                      <tr><td colSpan="3">No individual pieces found for this SKU.</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr><td colSpan="7">No recent activity found.</td></tr>
                )}
              </tbody>
            </table>
            <Pagination
              currentPage={globalLedgerCurrentPage}
              totalItems={globalLedgerTotalItems}
              itemsPerPage={globalLedgerItemsPerPage}
              onPageChange={handleGlobalLedgerPageChange}
            />
              </>
            )}
          </>
        )}
      </div>

      <div className="inventory-section">
        <button
          type="button"
          className="inventory-accordion-toggle"
          onClick={() => setShowSkuGroupSection((prev) => !prev)}
          aria-expanded={showSkuGroupSection}
          aria-controls="sku-group-accordion-content"
        >
          <span>SKU Group</span>
          <span className={`inventory-accordion-chevron ${showSkuGroupSection ? 'open' : ''}`}>▾</span>
        </button>
        {showSkuGroupSection && (
          <div id="sku-group-accordion-content" className="sku-group-accordion-content">
            <div className="sku-group-accordion-actions">
              <button
                type="button"
                className="sku-group-secondary-btn"
                onClick={() => fetchSkuGroups({ force: true })}
                disabled={isLoadingSkuGroups}
              >
                {isLoadingSkuGroups ? 'Refreshing...' : 'Refresh'}
              </button>
              <button
                type="button"
                className="sku-group-primary-btn"
                onClick={() => setShowSkuGroupForm((prev) => !prev)}
              >
                {showSkuGroupForm ? 'Close' : 'Add Group'}
              </button>
            </div>
            {showSkuGroupForm && (
              <div className="sku-group-grid">
                <div className="sku-group-card">
                  <h3>Create Group (Existing SKUs Only)</h3>
                  <div className="sku-group-form-row">
                    <input
                      type="text"
                      list="sku-group-sku-options"
                      placeholder="Search existing SKU"
                      value={skuInputValue}
                      onChange={(e) => handleSkuInputChange(e.target.value)}
                    />
                    {isSkuInputSearching && (
                      <div className="sku-group-input-meta searching">
                        <span className="inline-spinner" aria-hidden="true"></span>
                        Searching existing SKUs...
                      </div>
                    )}
                    {!isSkuInputSearching && hasSelectedDraftSku && (
                      <div className="sku-group-input-meta selected">Selected SKU: {selectedDraftSku}</div>
                    )}
                    {!isSkuInputSearching && String(skuInputValue || '').trim().length >= 2 && (
                      <div className="sku-group-suggestions-box">
                        {skuInputOptions.length > 0 ? (
                          skuInputOptions.map((opt) => (
                            <button
                              key={`sku-opt-${opt}`}
                              type="button"
                              className={`sku-group-suggestion-item ${selectedDraftSku === opt ? 'active' : ''}`}
                              onClick={() => setSkuInputValue(opt)}
                            >
                              {opt}
                            </button>
                          ))
                        ) : (
                          <div className="sku-group-suggestion-empty">No match found. You can still add this as a new SKU.</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="sku-group-draft-actions">
                    <button
                      type="button"
                      className="sku-group-secondary-btn"
                      onClick={addSkuToDraftGroup}
                      disabled={isSavingSkuGroup || !hasSelectedDraftSku}
                    >
                      {isSkuInputSearching ? (
                        <>
                          <span className="inline-spinner" aria-hidden="true"></span>
                          Searching...
                        </>
                      ) : (
                        'Add SKU'
                      )}
                    </button>
                  </div>
                  <div className="sku-group-chip-list">
                    {groupDraftSkus.length > 0 ? (
                      groupDraftSkus.map((sku) => (
                        <button
                          key={sku}
                          type="button"
                          className="sku-group-chip"
                          onClick={() => removeSkuFromDraftGroup(sku)}
                          title="Remove SKU"
                        >
                          {sku} ×
                        </button>
                      ))
                    ) : (
                      <span className="sku-group-empty">No SKU added yet.</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="sku-group-primary-btn"
                    onClick={createSkuGroupFromExistingSkus}
                    disabled={isSavingSkuGroup}
                  >
                    {isSavingSkuGroup ? 'Saving...' : 'Create Group'}
                  </button>
                </div>
              </div>
            )}
            <datalist id="sku-group-sku-options">
              {skuInputOptions.map((opt) => (
                <option key={`sku-${opt}`} value={opt} />
              ))}
            </datalist>
            {skuGroupMessage && <p className="sku-group-message">{skuGroupMessage}</p>}
            {isLoadingSkuGroups && <p>Loading SKU groups...</p>}
            {skuGroupsError && <p className="error-message">{skuGroupsError}</p>}
            {!isLoadingSkuGroups && !skuGroupsError && (
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Group ID</th>
                  <th>Name</th>
                  <th>Members (SKU / Stock)</th>
                  <th>Substitution</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                  {skuGroups.length > 0 ? (
                    skuGroups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.groupId || group.id}</td>
                        <td>{group.name || '-'}</td>
                        <td>
                          {Array.isArray(group.members) && group.members.length > 0 ? (
                            group.members.map((member) => (
                              <div key={`${group.id}-${member.sku}`} className="sku-group-member-row">
                                <span className="sku-group-member-sku">{member.sku}</span>
                                <span className="sku-group-member-stock">
                                  {Number(member?.stock?.totalLength || 0).toFixed(2)}m ({Number(member?.stock?.pieceCount || 0)} pcs)
                                </span>
                              </div>
                            ))
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{group.allowSubstitutionForOrders === false ? 'Disabled' : 'Enabled'}</td>
                        <td>{group.active === false ? 'Inactive' : 'Active'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5">No SKU groups found.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {selectedPiece && (
        <Modal isOpen={!!selectedPiece} onClose={() => setSelectedPiece(null)} contentClassName="history-modal-content">
          <h2>History for Piece: {selectedPiece.pieceId} (SKU: {selectedPiece.sku})</h2>
          {isFetchingHistory && <p>Loading history...</p>}
          {historyError && <p className="error-message">{historyError}</p>}
          {!isFetchingHistory && !historyError && (
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Change (m)</th>
                  <th>Reason/Order #</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pieceHistory.length > 0 ? (
                  pieceHistory.map((entry, index) => (
                    <tr key={entry.id || index}>
                      <td>{entry.timestamp.toLocaleString()}</td>
                      <td><span className={`ledger-type-tag ${entry.type.toLowerCase()}`}>{entry.type}</span></td>
                      <td className={entry.change > 0 ? 'positive-change' : 'negative-change'}>{entry.change.toFixed(2)}m</td>
                      <td>{formatReasonOrderText(entry)}</td>
                      <td>
                        {entry.type === 'ADJUSTMENT' && canPerformAction('inventory.undoAdjustment') && (
                          <button
                            onClick={() => handleUndoAdjustment(entry.id)}
                            disabled={isUndoingAdjustment && undoingLedgerId === entry.id}
                            className="action-button undo-action-btn"
                          >
                            {isUndoingAdjustment && undoingLedgerId === entry.id ? 'Undoing...' : 'Undo'}
                          </button>
                        )}
                        {canPerformAction('admin.purgeLegacyOrders') && (
                          <button
                            onClick={() => handleDeleteLedger(entry)}
                            className="action-button delete-ledger-btn"
                            disabled={deletingLedgerId === entry.id}
                          >
                            {deletingLedgerId === entry.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4">No history found for this piece.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {pieceToAdjust && (
        <Modal isOpen={!!pieceToAdjust} onClose={() => setPieceToAdjust(null)}>
          <h2>Adjust Stock for Piece</h2>
          <div className="adjustment-piece-info">
            <p><strong>SKU:</strong> {pieceToAdjust.sku}</p>
            <p><strong>Piece ID:</strong> {pieceToAdjust.pieceId}</p>
            <p><strong>Current Length:</strong> {pieceToAdjust.length.toFixed(2)}m</p>
          </div>
          {adjustmentError && <p className="error-message">{adjustmentError}</p>}
          <form onSubmit={handleAdjustSubmit} className="adjustment-form">
            <div className="form-group">
              <label htmlFor="adjustmentReason">Reason for Adjustment</label>
              <select
                id="adjustmentReason"
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                required
              >
                {allowedAdjustmentReasons.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.value}
                  </option>
                ))}
              </select>
            </div>
            {adjustmentReason === 'Correction' ? (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="correctionDeduct">Length to Deduct (m)</label>
                  <input
                    id="correctionDeduct"
                    type="number"
                    step="0.25"
                    min="0.25"
                    max={pieceToAdjust.length}
                    value={correctionDeduct}
                    onChange={(e) => setCorrectionDeduct(e.target.value)}
                    placeholder="e.g., 0.25"
                  />
                  <button
                    type="button"
                    className="adjustment-helper-btn"
                    onClick={() => setCorrectionDeduct(String(pieceToAdjust.length.toFixed(2)))}
                  >
                    Full qty
                  </button>
                </div>
                <div className="form-group">
                  <label htmlFor="correctionAdd">Length to Add (m)</label>
                  <input
                    id="correctionAdd"
                    type="number"
                    step="0.25"
                    min="0.25"
                    value={correctionAdd}
                    onChange={(e) => setCorrectionAdd(e.target.value)}
                    placeholder="e.g., 0.25"
                  />
                </div>
              </div>
            ) : (
              <div className="adjustment-amount-row">
                <div className="form-group">
                  <label htmlFor="adjustmentAmount">
                    {adjustmentReason === 'Split Piece' ? 'Length of New Piece (m)' : 'Length to Deduct (m)'}
                  </label>
                  <input
                    id="adjustmentAmount"
                    type="number"
                    step="0.25"
                    min="0.25"
                    max={adjustmentReason === 'Split Piece' ? pieceToAdjust.length : pieceToAdjust.length}
                    value={adjustmentAmount}
                    onChange={(e) => setAdjustmentAmount(e.target.value)}
                    required
                    placeholder="e.g., 1.5"
                  />
                </div>
                {(adjustmentReason === 'Lost' || adjustmentReason === 'Damaged') && (
                  <button
                    type="button"
                    className="adjustment-helper-btn"
                    onClick={() => setAdjustmentAmount(String(pieceToAdjust.length.toFixed(2)))}
                  >
                    Full qty
                  </button>
                )}
              </div>
            )}
            {adjustmentReason === 'Split Piece' && (
              <div className="form-group">
                <label htmlFor="newPieceIdForSplit">New Piece ID</label>
                <input
                  id="newPieceIdForSplit"
                  type="text"
                  value={newPieceIdForSplit}
                  onChange={(e) => setNewPieceIdForSplit(e.target.value)}
                  required
                  placeholder="ID for the new piece"
                />
              </div>
            )}
            {adjustmentReason === 'Use in VR Production' && (
              <div className="form-group">
                <label htmlFor="adjustmentRemark">Remark</label>
                <textarea
                  id="adjustmentRemark"
                  value={adjustmentRemark}
                  onChange={(e) => setAdjustmentRemark(e.target.value)}
                  required
                  placeholder="Enter VR production note"
                  rows={3}
                />
              </div>
            )}
            <button type="submit" disabled={isSubmittingAdjustment}>
              {isSubmittingAdjustment ? 'Adjusting...' : 'Confirm Adjustment'}
            </button>
          </form>
        </Modal>
      )}
        </>
      )}
    </div>
  );
}

export default InventoryPage;
